import React, { useCallback, useMemo, useState } from "react";
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Box,
  Card,
  Button,
  CircularProgress,
  Alert,
  Chip,
  InputBase,
} from "@mui/material";
import { ArrowBack, Add, Remove, DeleteOutline, PlaylistAdd, LocalFireDepartment } from "@mui/icons-material";
import { apiClient } from "../utils/http";
import { useAuth } from "../context/AuthContext";
import useMenu from "../hooks/useMenu";
import usePaginatedList from "../hooks/usePaginatedList";
import OrderCard from "../components/OrderCard";
import ProductCard from "../components/ProductCard";
import ProductOptionsSheet from "../components/ProductOptionsSheet";
import CancelReasonDialog from "../components/CancelReasonDialog";
import KotConfirmDialog from "../components/KotConfirmDialog";
import LoadMoreButton from "../components/LoadMoreButton";
import AutoLoadMore from "../components/AutoLoadMore";
import { isKotPrinted, orderItemsToEditLines, buildOrderPayload, linesNeedingReason, kotPrintLines, formatPrice, lineTotal } from "../utils/helpers";
import useDebouncedValue from "../hooks/useDebouncedValue";
import OrdersAppBar from "../components/OrdersAppBar";
import { NAV_HEIGHT } from "../components/MainLayout";

export default function LiveOrders() {
  const { store } = useAuth();
  const currency = store?.currency || store?.price_type || "";

  const [selectedOrder, setSelectedOrder] = useState(null);
  const [editLines, setEditLines] = useState([]);
  const [cancelledLines, setCancelledLines] = useState([]);
  const [reasonDialogOpen, setReasonDialogOpen] = useState(false); // shown once at Save time, not per-item
  const [kotConfirmOpen, setKotConfirmOpen] = useState(false); // shown at Save time when this save has new kitchen work
  const [pendingReason, setPendingReason] = useState(null);
  const [saving, setSaving] = useState(false);
  const [editError, setEditError] = useState("");

  const [printingBill, setPrintingBill] = useState(false);

  const [addingItem, setAddingItem] = useState(false);
  const [addSearch, setAddSearch] = useState("");
  const [activeProduct, setActiveProduct] = useState(null);
  const menu = useMenu({ search: addSearch });

  const [orderSearch, setOrderSearch] = useState("");
  const debouncedOrderSearch = useDebouncedValue(orderSearch);

  // GET pos/v1/orders/get-orders?st=live&search=...&page=N — OrderController@getOrders
  // filters to ['processing','modifying','dispatched'] server-side (st=live), so
  // each page is already just live orders — no client-side status filtering or
  // multi-page walking needed. search filters by order_no, order_type,
  // customer name, or product name server-side.
  const fetchLivePage = useCallback(
    (page) =>
      apiClient.get("orders/get-orders", { params: { st: "live", search: debouncedOrderSearch, page } }).then((res) => {
        const paginator = res.data?.orders || {};
        return { items: paginator.data || [], hasMore: (paginator.current_page || 1) < (paginator.last_page || 1) };
      }),
    [debouncedOrderSearch]
  );
  const { items: orders, loading, loadingMore, hasMore, error, loadMore, reload: loadOrders } = usePaginatedList(fetchLivePage, [debouncedOrderSearch]);

  // orders/modify only accepts orders in 'placed'/'processing'/'modifying' status
  // (OrderController@modifyOrder) — 'dispatched' shows up in Live Orders now too
  // (out-for-delivery is still in-progress) but isn't editable through this flow,
  // so it's opened read-only instead of calling modify and hitting a 404.
  const isDispatched = selectedOrder?.status === "dispatched";

  const openOrder = async (order) => {
    setEditError("");
    setSelectedOrder(order);
    setEditLines(orderItemsToEditLines(order.order_items));
    setCancelledLines([]);
    // Defensive: a stuck true here (e.g. a save/print action's success path
    // that navigated away without resetting its own flag) would otherwise
    // render this next order's Save/Print Bill button as already in-flight.
    setSaving(false);
    setPrintingBill(false);
    if (order.status === "dispatched") return;
    try {
      // POST pos/v1/orders/modify — { order: <id> } — toggles processing -> modifying;
      // placeOrder only accepts orders in 'created' or 'modifying' status, so this has
      // to happen before any add/remove/save on an already-live order.
      await apiClient.post("orders/modify", { order: order.id });
    } catch (err) {
      setEditError(err.message || "Failed to open this order for editing");
    }
  };

  const closeOrder = async (discard) => {
    if (discard && selectedOrder && selectedOrder.status !== "dispatched") {
      // POST pos/v1/orders/cancel-modify — releases the 'modifying' lock without
      // saving anything, since nothing in this edit session was persisted yet.
      apiClient.post("orders/cancel-modify", { order: selectedOrder.id }).catch(() => {});
    }
    setSelectedOrder(null);
    setEditLines([]);
    setCancelledLines([]);
    setReasonDialogOpen(false);
    loadOrders();
  };

  // Removing (or reducing — see updateQuantity) a line no longer prompts for a
  // reason immediately: the waiter makes all their changes first, and a single
  // reason is asked for once, at Save time, covering everything that needs one
  // (see handleSave/linesNeedingReason) — not interrupting mid-edit per item.
  const handleRemoveLine = (line) => {
    // A line added earlier in this same edit session (no orderItemId yet) just
    // disappears — there's nothing to tell the backend about. An existing
    // (already-saved) line needs to be marked cancelled either way; whether
    // that needs a reason is decided later, at Save time, from kotPrintedQty.
    if (line.orderItemId) {
      setCancelledLines((prev) => [...prev, { orderItemId: line.orderItemId, productName: line.product.name, kotPrintedQty: line.kotPrintedQty }]);
    }
    setEditLines((prev) => prev.filter((l) => l.cartId !== line.cartId));
  };

  const updateQuantity = (line, quantity) => {
    if (quantity <= 0) {
      handleRemoveLine(line);
      return;
    }
    setEditLines((prev) => prev.map((l) => (l.cartId === line.cartId ? { ...l, quantity } : l)));
  };

  const handleAddNewItem = ({ product, option, addons, quantity, notes }) => {
    setEditLines((prev) => [
      ...prev,
      { cartId: `new_${Date.now()}`, orderItemId: null, product, option, addons, quantity, notes, kotPrintedQty: 0 },
    ]);
    setActiveProduct(null);
    setAddingItem(false);
  };

  const savePrompt = linesNeedingReason(editLines, cancelledLines);

  // New line (added this session, no orderItemId yet) or an existing line's
  // quantity raised above what it started at — either means new work the
  // kitchen hasn't seen yet, so this save should also trigger a KOT print
  // rather than just persist quietly.
  const hasNewKitchenWork = editLines.some((l) => !l.orderItemId || (l.originalQuantity != null && l.quantity > l.originalQuantity));

  // Nothing to save at all unless a line was added, removed, or its quantity
  // actually changed from what the order started this edit session at — the
  // Save button only shows once one of those has actually happened.
  const isDirty =
    cancelledLines.length > 0 ||
    editLines.some((l) => !l.orderItemId || (l.originalQuantity != null && l.quantity !== l.originalQuantity));

  const handleSaveClick = () => {
    if (savePrompt.any) {
      setReasonDialogOpen(true);
      return;
    }
    if (hasNewKitchenWork) {
      setKotConfirmOpen(true);
      return;
    }
    doSave(null);
  };

  const handleReasonConfirm = (reason) => {
    setReasonDialogOpen(false);
    if (hasNewKitchenWork) {
      setPendingReason(reason);
      setKotConfirmOpen(true);
      return;
    }
    doSave(reason);
  };

  const handleKotConfirm = () => {
    setKotConfirmOpen(false);
    doSave(pendingReason);
    setPendingReason(null);
  };

  const doSave = async (sharedReason) => {
    setSaving(true);
    setEditError("");
    try {
      const payload = buildOrderPayload({
        cart: editLines,
        orderType: selectedOrder.order_type,
        table: selectedOrder.store_table ? { id: selectedOrder.store_table_id } : null,
        deliveryAddress: selectedOrder.delivery_address,
        // This order's already-linked customer, if any — must be preserved on
        // every save, not reset, or placeOrder's own validation (contact_number
        // required whenever store_customer_id is null) rejects an edit to an
        // order that already has a customer, since it validates this payload,
        // not what's already saved on the order.
        storeCustomerId: selectedOrder.store_customer_id,
        customerName: selectedOrder.name,
        contactNumber: selectedOrder.contact_number,
        orderNote: selectedOrder.order_note,
        existingOrderId: selectedOrder.id,
        cancelledLines,
        sharedReason,
      });
      // POST pos/v1/orders/place — same endpoint used for a brand new order;
      // when the order is already 'modifying' this updates/adds/cancels lines
      // and flips status back to 'processing' (OrderController.php:191,322).
      // kot_print (a sibling field, like st) asks the backend to also dispatch
      // the KOT print in-process, in this same request, right after saving —
      // one round trip instead of two.
      const placeRes = await apiClient.post("orders/place", {
        order: JSON.stringify(payload),
        kot_print: hasNewKitchenWork ? 1 : 0,
      });

      if (placeRes.data?.kot_print_error) {
        // The order itself is already safely saved at this point — only the
        // print failed, so stay on this screen (rather than navigating back to
        // the list, which would bury this message) and let the waiter retry
        // Save (harmless — placeOrder just re-applies the same, already-current
        // state) or leave manually.
        //
        // Refresh from the saved order the response already carries, rather
        // than leaving the pre-save editLines in place: a line added this
        // session has no orderItemId yet, so until now it's been priced via
        // lineTotal() (raw product price, no tax) — the same line just got a
        // real orderItemId + tax-inclusive total_price from placeOrder, and
        // showing anything else here would mix un-taxed and taxed prices in
        // the same order.
        const updatedOrder = placeRes.data?.order;
        if (updatedOrder) {
          setSelectedOrder(updatedOrder);
          setEditLines(orderItemsToEditLines(updatedOrder.order_items));
          setCancelledLines([]);
        }
        setEditError(`Order saved, but printing failed: ${placeRes.data.kot_print_error}`);
        return;
      }

      await closeOrder(false);
    } catch (err) {
      setEditError(err.message || "Failed to save changes");
    } finally {
      setSaving(false);
    }
  };

  // Printing the bill is what hands a processing order off — mirrors denzo-pos's
  // own "Print Bill" (OrderDetails.js@handlePrint): it flips the order straight
  // to 'dispatched' rather than being a separate status-change step. Only offered
  // once there are no unsaved edits, so it doesn't have to duplicate Save's
  // cancel-reason prompt for reduced/removed lines — Save that first.
  const handlePrintBill = async () => {
    setPrintingBill(true);
    setEditError("");
    try {
      await apiClient.post("orders/update-ord-status", {
        id: selectedOrder.id,
        status: "dispatched",
        received: selectedOrder.received_amount,
        discount_percent: selectedOrder.discount_percent,
      });
    } catch (err) {
      setEditError(err.message || "Failed to update order status");
      setPrintingBill(false);
      return;
    }
    try {
      // GET pos/v1/orders/print — CommonController@printOrder — same in-process
      // print dispatch (via the store's configured print agent) the KOT print
      // uses; no client-side print plugin needed.
      await apiClient.get("orders/print", {
        params: { ord: selectedOrder.order_no, str: selectedOrder.store_id, type: "normal", system: "posweb" },
      });
    } catch (err) {
      // Status change already went through — only the print failed. Same
      // reasoning as kot_print_error in doSave: don't leave the waiter thinking
      // nothing happened.
      setEditError(`Order dispatched, but printing failed: ${err.message || "unknown error"}`);
      setPrintingBill(false);
      return;
    }
    setPrintingBill(false);
    await closeOrder(false);
  };

  const editTotal = useMemo(
    () => editLines.reduce((sum, l) => sum + (l.orderItemId ? l.totalPrice : lineTotal(l)), 0),
    [editLines]
  );

  if (selectedOrder) {
    return (
      <Box sx={{ pb: `${112 + NAV_HEIGHT}px` }}>
        <AppBar position="sticky" color="default" elevation={0} sx={{ bgcolor: "#fff", borderBottom: "1px solid", borderColor: "divider" }}>
          <Toolbar>
            <IconButton edge="start" onClick={() => closeOrder(true)}>
              <ArrowBack />
            </IconButton>
            <Typography variant="h6" fontWeight={700} sx={{ flex: 1 }}>
              #{selectedOrder.order_no || selectedOrder.id}
            </Typography>
            {selectedOrder.order_type === "dine_in" && selectedOrder.store_table && <Chip label={selectedOrder.store_table.name} color="primary" size="small" />}
          </Toolbar>
        </AppBar>

        <Box sx={{ p: 2 }}>
          {editError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {editError}
            </Alert>
          )}
          {isDispatched && (
            <Alert severity="info" sx={{ mb: 2 }}>
              This order has already been dispatched — view only.
            </Alert>
          )}

          {editLines.map((line) => (
            <Card key={line.cartId} variant="outlined" sx={{ p: 1.5, mb: 1.25, borderRadius: 2 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                    <Typography variant="subtitle2" fontWeight={700}>
                      {line.product.name}
                    </Typography>
                    {isKotPrinted(line) && (
                      <Chip
                        icon={<LocalFireDepartment sx={{ fontSize: 14 }} />}
                        label="Sent to kitchen"
                        size="small"
                        color="warning"
                        sx={{ height: 20, fontSize: 10 }}
                      />
                    )}
                  </Box>
                  {line.option && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      {line.option.name}
                    </Typography>
                  )}
                  {line.addons && line.addons.length > 0 && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      {line.addons.map((adn) => `${adn.name}:${adn.quantity}`).join(", ")}
                    </Typography>
                  )}
                </Box>
                {!isDispatched && (
                  <IconButton size="small" onClick={() => handleRemoveLine(line)}>
                    <DeleteOutline fontSize="small" />
                  </IconButton>
                )}
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: 1 }}>
                {isDispatched ? (
                  <Typography variant="body2" color="text.secondary">
                    Qty: {line.quantity}
                  </Typography>
                ) : (
                  <Box sx={{ display: "flex", alignItems: "center", border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
                    <IconButton size="small" onClick={() => updateQuantity(line, line.quantity - 1)}>
                      <Remove fontSize="small" />
                    </IconButton>
                    <Typography sx={{ px: 1.5, minWidth: 20, textAlign: "center" }}>{line.quantity}</Typography>
                    <IconButton size="small" onClick={() => updateQuantity(line, line.quantity + 1)}>
                      <Add fontSize="small" />
                    </IconButton>
                  </Box>
                )}
                <Typography fontWeight={700}>
                  {formatPrice(line.orderItemId ? line.totalPrice : lineTotal(line), currency)}
                </Typography>
              </Box>
            </Card>
          ))}

          {!isDispatched && (
            <Button fullWidth variant="outlined" startIcon={<PlaylistAdd />} onClick={() => setAddingItem(true)} sx={{ mt: 1 }}>
              Add Items
            </Button>
          )}
        </Box>

        <Box sx={{ position: "fixed", left: 0, right: 0, bottom: NAV_HEIGHT, bgcolor: "#fff", borderTop: "1px solid", borderColor: "divider", p: 2, zIndex: 15 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: !isDispatched ? 1.5 : 0 }}>
            <Typography variant="h6">Total</Typography>
            <Typography variant="h6" fontWeight={800}>
              {formatPrice(editTotal, currency)}
            </Typography>
          </Box>
          {!isDispatched && isDirty && (
            <Button fullWidth variant="contained" size="large" disabled={saving} onClick={handleSaveClick} sx={{ py: 1.4 }}>
              {saving ? "Saving..." : hasNewKitchenWork ? "Save & KOT Print" : "Save Changes"}
            </Button>
          )}
          {!isDispatched && !isDirty && (
            <Button fullWidth variant="contained" size="large" disabled={printingBill} onClick={handlePrintBill} sx={{ py: 1.4 }}>
              {printingBill ? "Printing..." : "Print Bill"}
            </Button>
          )}
        </Box>

        {addingItem && (
          <Box sx={{ position: "fixed", inset: 0, bgcolor: "#fff", zIndex: 30, overflowY: "auto" }}>
            <AppBar position="sticky" color="default" elevation={0} sx={{ bgcolor: "#fff", borderBottom: "1px solid", borderColor: "divider" }}>
              <Toolbar>
                <IconButton edge="start" onClick={() => setAddingItem(false)}>
                  <ArrowBack />
                </IconButton>
                <Typography variant="h6" fontWeight={700}>
                  Add Items
                </Typography>
              </Toolbar>
            </AppBar>
            <Box sx={{ p: 2 }}>
              <InputBase
                placeholder="Search menu"
                fullWidth
                value={addSearch}
                onChange={(e) => setAddSearch(e.target.value)}
                sx={{ mb: 2, bgcolor: "grey.100", borderRadius: 3, px: 1.5, py: 0.8 }}
              />
              {menu.loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <>
                  {menu.products.map((p) => (
                    <ProductCard key={p.id} product={p} currency={currency} onPress={setActiveProduct} />
                  ))}
                  <AutoLoadMore hasMore={menu.hasMore} loadingMore={menu.loadingMore} onLoadMore={menu.loadMoreProducts} />
                </>
              )}
            </Box>
          </Box>
        )}

        <ProductOptionsSheet product={activeProduct} currency={currency} onClose={() => setActiveProduct(null)} onConfirm={handleAddNewItem} />
        <CancelReasonDialog
          open={reasonDialogOpen}
          items={[
            ...savePrompt.reducedLines.map((l) => `${l.product.name} — reduced to ${l.quantity} (from ${l.originalQuantity})`),
            ...savePrompt.removedLines.map((c) => `${c.productName} — removed`),
          ]}
          onCancel={() => setReasonDialogOpen(false)}
          onConfirm={handleReasonConfirm}
        />
        <KotConfirmDialog
          open={kotConfirmOpen}
          items={kotPrintLines(editLines)}
          onCancel={() => {
            setKotConfirmOpen(false);
            setPendingReason(null);
          }}
          onConfirm={handleKotConfirm}
        />
      </Box>
    );
  }

  return (
    <Box>
      <OrdersAppBar title="Live Orders" search={orderSearch} onSearchChange={setOrderSearch} />

      <Box sx={{ p: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress />
          </Box>
        ) : orders.length === 0 ? (
          <Typography color="text.secondary" align="center" sx={{ py: 6 }}>
            {debouncedOrderSearch ? "No matching orders." : "No live orders right now."}
          </Typography>
        ) : (
          <>
            {orders.map((o) => (
              <OrderCard key={o.id} order={o} currency={currency} onPress={openOrder} />
            ))}
            <LoadMoreButton hasMore={hasMore} loadingMore={loadingMore} onClick={loadMore} />
          </>
        )}
      </Box>
    </Box>
  );
}
